/**
 * FoodPreferencesStep.constants.js
 *
 * Option data for Screen 7 "Food Preferences". id is the stable value
 * persisted to the assessment record and used to query the Nutrition
 * Database downstream. Same shape as GoalStep.js's GOAL_OPTIONS.
 *
 * Filter-mode correction (FSD v2.1 §6 "Seven-Day Plan Definition"):
 * "Budget and cooking time are soft-ranking factors unless explicitly
 * configured as hard constraints." The original build treated Cooking
 * Time and Budget as hard database-query filters (see the removed
 * comment block below) — that's no longer the closed baseline. Diet
 * Type is unaffected: it stays a hard filter, governed by the
 * deterministic diet-safety engine (DEC-12; e.g. Jain excludes root
 * vegetables and onion/garlic at the query level). Cuisine was already
 * correctly a soft ranking preference.
 *
 * FILTER_MODE / *_FILTER_MODE below are exported so the Rule Engine
 * reads filter strictness from this single source rather than each
 * consumer guessing from field name alone — same pattern as
 * ACTIVITY_MULTIPLIER_RULE / MEDICAL_CATALOGUE_RULE in the other step
 * constants files.
 */

export const FILTER_MODE = {
  HARD: "hard", // Query-level exclusion — candidate pool never contains a violation.
  SOFT: "soft", // Ranking preference — used to prioritize, not exclude.
};

export const FOOD_PREFERENCES_FILTER_RULE = {
  ruleId: "MEAL-FILTERMODE-001",
  source: "FSD v2.1 §6 Seven-Day Plan Definition",
  version: "2.1",
};

export const DIET_TYPE_FILTER_MODE = FILTER_MODE.HARD;
export const CUISINE_FILTER_MODE = FILTER_MODE.SOFT;
export const COOKING_TIME_FILTER_MODE = FILTER_MODE.SOFT;
export const BUDGET_FILTER_MODE = FILTER_MODE.SOFT;

export const DIET_TYPE_OPTIONS = [
  { id: "vegetarian", label: "Vegetarian" },
  { id: "non_vegetarian", label: "Non-Vegetarian" },
  { id: "eggetarian", label: "Eggetarian" },
  { id: "vegan", label: "Vegan" },
  { id: "jain", label: "Jain" },
];

export const CUISINE_OPTIONS = [
  { id: "north_indian", label: "North Indian" },
  { id: "south_indian", label: "South Indian" },
  { id: "gujarati", label: "Gujarati" },
  { id: "punjabi", label: "Punjabi" },
  { id: "bengali", label: "Bengali" },
  { id: "rajasthani", label: "Rajasthani" },
  { id: "continental", label: "Continental" },
  { id: "chinese", label: "Chinese" },
];

// Maps to recipe cooking_time_band in the Nutrition Database. Soft
// ranking factor by default (FSD v2.1 §6) — used to prioritize
// candidates within the approved time band, not to hard-exclude
// everything outside it, unless a hard-constraint mode is explicitly
// configured. First entry is the unselected placeholder — id "" fails
// validation until the user picks a real band, mirroring the
// required-select pattern.
export const COOKING_TIME_OPTIONS = [
  { id: "", label: "Select cooking time" },
  { id: "under_15", label: "Under 15 minutes" },
  { id: "15_30", label: "15–30 minutes" },
  { id: "30_60", label: "30–60 minutes" },
  { id: "60_plus", label: "60+ minutes" },
];

// Maps to recipe budget_category in the Nutrition Database. Soft
// ranking factor by default (FSD v2.1 §6), not a hard database-query
// exclusion. `value` is the slider's numeric position (0–2); `id` is
// the persisted value.
export const BUDGET_LEVELS = [
  { id: "budget", label: "Budget", value: 0 },
  { id: "moderate", label: "Moderate", value: 1 },
  { id: "premium", label: "Premium", value: 2 },
];

// Where the slider thumb visually sits before the user has interacted
// with it. formData.budget itself stays null until they do (see
// FoodPreferencesStep.js), so Next remains correctly disabled even though
// the thumb isn't shown at the leftmost position.
export const BUDGET_DEFAULT_VISUAL_VALUE = 1;

export const FOOD_PREFERENCES_INITIAL_DATA = {
  dietType: null,
  cuisines: [],
  cookingTime: "",
  budget: null,
};